// Data in the vault is a log of what was transferred, not the state of a database: a value is what a
// system last sent, and it gets old. How old is judged against how often it usually comes.

export type Freshness = 'fresh' | 'aging' | 'stale';

/** Fresh within twice the usual interval, aging up to six times it, stale after; null when there is no usual interval. */
export function freshness(last: number | null | undefined, usualMs: number | null | undefined, now: number): Freshness | null {
  if (!last || !usualMs || usualMs <= 0) return null;
  const usual = Math.max(60_000, usualMs);
  const age = now - last;
  return age <= 2 * usual ? 'fresh' : age <= 6 * usual ? 'aging' : 'stale';
}

export const FRESHNESS: Record<Freshness, { label: string; dot: string; text: string }> = {
  fresh: { label: 'up to date', dot: 'bg-ok-dot', text: 'text-ok' },
  aging: { label: 'getting old', dot: 'bg-hold-dot', text: 'text-hold' },
  stale: { label: 'out of date', dot: 'bg-line-strong', text: 'text-faint' },
};

/** "2 h", "41 d": how long ago, short. */
export function age(ms: number, now: number): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}

/** The usual interval between the documents of a kind of record, from how many came and over how long. */
export function usualInterval(first: number | null | undefined, last: number | null | undefined, documents: number): number | null {
  if (!first || !last || documents < 3 || last <= first) return null;
  return (last - first) / (documents - 1);
}
